import { CartItem } from '../../core/models/sale.model';
import { holdsOwnStock } from '../../core/pos/line-stock-availability';

/**
 * Minimal current-stock view of a product at a location — the shape the POS gets back from
 * `ProductService.getProductsByIds(...)`. Kept structural so this util stays decoupled from the
 * service's richer `ProductWithStock` type.
 */
export interface CurrentStock {
  id?: string;
  /** Current stock at the location for a simple product (undefined for variant products). */
  stockAtLocation?: number;
  /** Per-variant current stock at the location, for products with variants. */
  variants?: { id?: string; stockAtLocation?: number }[];
}

/** A resumed cart line whose quantity now exceeds the current stock at the location. */
export interface StockShortfall {
  name: string;
  requested: number;
  available: number;
}

/**
 * Compute which resumed cart lines now exceed current stock at the location (ADR-0007). Held
 * orders carry an add-time stock snapshot, so on resume we compare each line's quantity against
 * freshly-read stock. This is advisory only — the hard stock gate stays at checkout.
 *
 * Lines that don't map 1:1 to a single stock holder are skipped: services, the synthetic
 * surcharge line, combos and presentations (their components/base product hold the stock), and
 * return lines (quantity <= 0). Lines whose product wasn't found in `products` are skipped too —
 * we only warn on a genuine, measurable shortfall.
 */
export function computeStockShortfalls(
  items: CartItem[],
  products: CurrentStock[]
): StockShortfall[] {
  const byId = indexById(products);
  const shortfalls: StockShortfall[] = [];

  for (const item of items) {
    if (!holdsOwnStock(item)) {
      continue;
    }
    const requested = Number(item.quantity) || 0;
    if (requested <= 0) {
      continue;
    }

    const available = resolveCurrentStock(item, byId);
    if (available === undefined) {
      continue;
    }

    if (requested > available) {
      shortfalls.push({ name: item.name, requested, available });
    }
  }

  return shortfalls;
}

/** Index a freshly-read stock view by product id, skipping anything without one. */
function indexById(products: CurrentStock[]): Map<string, CurrentStock> {
  return new Map(products.filter(p => p.id).map(p => [p.id!, p]));
}

/**
 * What the freshly-read view says this line's holder has at the location, or `undefined` when
 * the read simply cannot answer.
 *
 * The distinction matters and mirrors `line-stock-availability`: a holder we can see but that
 * carries no figure is *registered nowhere* and really does have 0, while a holder missing from
 * the read — an unread product, or a variant that is gone from the ones we got — is **unknown**.
 * Collapsing the second into 0 would invent a shortage out of a read we never made.
 */
function resolveCurrentStock(item: CartItem, byId: Map<string, CurrentStock>): number | undefined {
  const product = item.id ? byId.get(item.id) : undefined;
  if (!product) {
    return undefined;
  }
  if (!item.variantId) {
    return product.stockAtLocation ?? 0;
  }

  const variant = product.variants?.find(v => v.id === item.variantId);
  return variant ? (variant.stockAtLocation ?? 0) : undefined;
}

/**
 * Re-stamp each cart line's `stockAtLocation` from a freshly-read view of the location's stock.
 *
 * The snapshot on a cart line is captured once, at add-to-cart, and the POS cart survives a
 * reload through localStorage — so on load it can be arbitrarily old. It is not a cosmetic
 * number: the cart badge renders it, and `resolveLineStockAvailability` deliberately lets a
 * non-zero line snapshot win over the catalog, so a stale one also skews the Cobrar pre-check.
 * Re-stamping it on load is what keeps both honest.
 *
 * Only lines that hold stock 1:1 are touched ({@link holdsOwnStock}), and only when the read can
 * actually answer for them ({@link resolveCurrentStock}) — a line whose holder is missing from
 * the read keeps its existing snapshot, because absence means "not read", not "no stock".
 * Sharing both rules with {@link computeStockShortfalls} is what keeps the warning and the
 * number it corrects from ever disagreeing.
 *
 * Returns a new array only when something actually changed, so an unchanged cart doesn't churn
 * the signal (and therefore doesn't rewrite localStorage).
 */
export function refreshCartItemStock(items: CartItem[], products: CurrentStock[]): CartItem[] {
  const byId = indexById(products);
  let changed = false;

  const refreshed = items.map(item => {
    if (!holdsOwnStock(item)) {
      return item;
    }

    const fresh = resolveCurrentStock(item, byId);
    if (fresh === undefined) {
      return item;
    }
    // Note the second half: a line whose figure did not move still has to be *marked* measured
    // the first time we read it, or a genuine 0 stays indistinguishable from the add-to-cart
    // default and the Cobrar pre-check keeps handing it back to the catalog.
    if (fresh === item.stockAtLocation && item.stockMeasured) {
      return item;
    }

    changed = true;
    return { ...item, stockAtLocation: fresh, stockMeasured: true };
  });

  return changed ? refreshed : items;
}

/**
 * Build a short, non-blocking warning message for resumed lines short on stock. Kept under the
 * snackbar's ~100-char budget: a single shortfall is spelled out; several are summarized.
 */
export function buildStockShortfallMessage(shortfalls: StockShortfall[]): string {
  if (shortfalls.length === 0) {
    return '';
  }
  if (shortfalls.length === 1) {
    const s = shortfalls[0];
    return `«${s.name}»: disponible ${s.available}, en carrito ${s.requested}`;
  }
  return `${shortfalls.length} productos superan el stock disponible`;
}
